import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Users, TrendingUp, Award, ArrowRight } from 'lucide-react';

const SuccessStoriesSection = () => {
  const { language } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [hoveredCard, setHoveredCard] = useState(null);

  const stories = [
    {
      slug: 'tesla',
      title: language === 'fr' ? 'Lancement Produit Tesla' : 'Tesla Product Launch',
      tag: language === 'fr' ? 'Lancement de marque' : 'Brand Launch',
      description: language === 'fr'
        ? 'Événement exclusif avec expérience immersive. Coverage médiatique international.'
        : 'Exclusive event with immersive experience. International media coverage.',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=800&q=80',
      stats: {
        guests: 300,
        satisfaction: 99,
        roi: '+520%',
      },
    },
    {
      slug: 'gala-magazine',
      title: language === 'fr' ? 'Soirée Gala Magazine' : 'Magazine Gala Evening',
      tag: language === 'fr' ? 'Soirée privée' : 'Private Evening',
      description: language === 'fr'
        ? 'Soirée de prestige réunissant influenceurs et personnalités. Couverture presse nationale.'
        : 'Prestigious evening bringing together influencers and personalities. National press coverage.',
      image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=800&q=80',
      stats: {
        guests: 500,
        media: 15,
        roi: '+350%',
      },
    },
    {
      slug: 'la-maison',
      title: language === 'fr' ? 'Inauguration La Maison' : 'La Maison Inauguration',
      tag: language === 'fr' ? 'Activation digitale' : 'Digital Activation',
      description: language === 'fr'
        ? 'Ouverture exclusive d\'un établissement haut de gamme avec activation digitale live.'
        : 'Exclusive opening of a high-end establishment with live digital activation.',
      image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=800&q=80',
      stats: {
        guests: 200,
        satisfaction: 98,
        roi: '+280%',
      },
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 80,
        damping: 15,
      },
    },
  };

  return (
    <section
      id="realisations"
      ref={ref}
      className="py-24 md:py-32 bg-white"
      data-testid="success-stories-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-block text-xs font-semibold tracking-[0.3em] uppercase text-neutral-500 mb-4"
          >
            {language === 'fr' ? 'Success Stories' : 'Success Stories'}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="heading-xl text-4xl md:text-5xl lg:text-6xl mb-4 text-black"
            data-testid="success-stories-title"
          >
            {language === 'fr' ? 'Nos réalisations' : 'Our achievements'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="text-lg text-neutral-600 max-w-2xl mx-auto"
          >
            {language === 'fr'
              ? 'À chaque projet, une réponse sur mesure.'
              : 'For each project, a tailor-made solution.'}
          </motion.p>
        </div>

        {/* Stories Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
        >
          {stories.map((story, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              onHoverStart={() => setHoveredCard(index)}
              onHoverEnd={() => setHoveredCard(null)}
              whileHover={{ y: -12 }}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg border border-neutral-100"
              data-testid={`success-story-${index}`}
            >
              {/* Clickable Image */}
              <Link to={`/projets/${story.slug}`} className="block relative h-56 md:h-64 overflow-hidden cursor-pointer">
                <motion.img
                  src={story.image}
                  alt={story.title}
                  className="w-full h-full object-cover"
                  animate={{
                    scale: hoveredCard === index ? 1.08 : 1,
                  }}
                  transition={{ duration: 0.5 }}
                />
                
                {/* Tag Badge */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full text-xs font-semibold text-black"
                >
                  {story.tag}
                </motion.div>

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              </Link>

              {/* Content */}
              <div className="p-6 md:p-8">
                {/* Clickable Title */}
                <Link to={`/projets/${story.slug}`}>
                  <h3 className="font-serif text-xl md:text-2xl font-bold text-black mb-3 hover:text-neutral-600 transition-colors cursor-pointer">
                    {story.title}
                  </h3>
                </Link>
                <p className="text-neutral-600 mb-6 leading-relaxed">
                  {story.description}
                </p>

                {/* Stats */}
                <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-neutral-100">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-neutral-400" />
                    <span className="text-sm font-semibold text-black">
                      {story.stats.guests} {language === 'fr' ? 'invités' : 'guests'}
                    </span>
                  </div>
                  {story.stats.satisfaction && (
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-neutral-400" />
                      <span className="text-sm font-semibold text-black">
                        {story.stats.satisfaction}% {language === 'fr' ? 'satisfaction' : 'satisfaction'}
                      </span>
                    </div>
                  )}
                  {story.stats.media && (
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-neutral-400" />
                      <span className="text-sm font-semibold text-black">
                        {story.stats.media} {language === 'fr' ? 'médias' : 'media'}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-semibold text-green-600">
                      ROI {story.stats.roi}
                    </span>
                  </div>
                </div>

                {/* View Project Link */}
                <Link
                  to={`/projets/${story.slug}`}
                  className="inline-flex items-center gap-2 text-black font-semibold hover:text-neutral-600 transition-colors mb-4 group/link"
                  data-testid={`view-project-${index}`}
                >
                  <span>{language === 'fr' ? 'Voir le projet' : 'View project'}</span>
                  <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                </Link>

                {/* CTA Button */}
                <motion.a
                  href="#contact"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center gap-2 w-full btn-pill bg-black text-white font-semibold py-4"
                  data-testid={`success-story-cta-${index}`}
                >
                  {language === 'fr' ? 'Créer un événement similaire' : 'Create a similar event'}
                  <motion.div
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight className="w-4 h-4" />
                  </motion.div>
                </motion.a>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default SuccessStoriesSection;
